/**
 * Search patterns: the routes an aircraft flies to cover ground rather than
 * travel across it.
 *
 * Laying a lawnmower by hand is a dozen clicks that have to be evenly spaced,
 * and the spacing is the part that matters — it is what decides whether the
 * sensor actually saw everything between the lines. So it is a number the
 * operator sets, not a distance they eyeball.
 *
 * Pure: an area and a spacing in, waypoints out. No map, no store, no aircraft.
 *
 * Flat-earth about a local origin, like everything else at this scale: an
 * operating area is kilometres across, where the error from ignoring curvature
 * is far below the spacing anyone would fly.
 */

import { pointInPolygon } from '@/map/zoneGeometry';
import { MavCmd, MavFrame, type Waypoint } from '@/types/domain';

export type PatternKind = 'grid' | 'expanding-square' | 'spiral' | 'sector' | 'orbit';

export interface LatLon {
  lat: number;
  lon: number;
}

export interface PatternSpec {
  kind: PatternKind;
  /** Centre for the radial patterns; the area's middle for a grid. */
  center: LatLon;
  /** How far out the pattern reaches, metres. */
  radiusM: number;
  /** Distance between adjacent passes, metres. The sensor footprint decides it. */
  spacingM: number;
  /** Altitude for every waypoint, metres relative to launch. */
  altM: number;
  /** Grid orientation, degrees from north. Fly the long axis of the area. */
  headingDeg?: number;
  /** Optional area to clip a grid to. Without it, the radius gives a square. */
  area?: LatLon[];
}

export class PatternError extends Error {}

const METRES_PER_DEG_LAT = 111_320.0;
/** Above this a pattern is refused rather than freezing the panel building it. */
export const MAX_WAYPOINTS = 600;

function offset(origin: LatLon, eastM: number, northM: number): LatLon {
  const lat = origin.lat + northM / METRES_PER_DEG_LAT;
  const lon =
    origin.lon +
    eastM / (METRES_PER_DEG_LAT * Math.max(0.01, Math.cos((origin.lat * Math.PI) / 180)));
  return { lat, lon };
}

function waypoint(point: LatLon, altM: number): Waypoint {
  return {
    lat: point.lat,
    lon: point.lon,
    alt: altM,
    command: MavCmd.NavWaypoint,
    frame: MavFrame.GlobalRelativeAlt,
    param1: 0,
    param2: 0,
    param3: 0,
    param4: 0,
    autocontinue: true,
  };
}

/** Rotate a local east/north offset by a heading measured from north. */
function rotate(eastM: number, northM: number, headingDeg: number): [number, number] {
  const r = (headingDeg * Math.PI) / 180;
  return [eastM * Math.cos(r) + northM * Math.sin(r), -eastM * Math.sin(r) + northM * Math.cos(r)];
}

/**
 * Parallel passes back and forth — the lawnmower.
 *
 * Alternate rows run in opposite directions so the aircraft never flies an
 * empty return leg, which on a large area is most of the battery.
 */
function grid(spec: PatternSpec): LatLon[] {
  const { center, radiusM, spacingM, headingDeg = 0, area } = spec;
  const points: LatLon[] = [];
  let flip = false;
  for (let north = -radiusM; north <= radiusM; north += spacingM) {
    const row: LatLon[] = [];
    for (let east = -radiusM; east <= radiusM; east += spacingM) {
      const [e, n] = rotate(east, north, headingDeg);
      const point = offset(center, e, n);
      // Clipping by keeping only the points inside beats clipping segments:
      // an operating area is not always convex, and a pass that leaves and
      // re-enters should be two passes, not one line straight across the gap.
      if (area && !pointInPolygon(point.lat, point.lon, area)) continue;
      row.push(point);
    }
    if (row.length === 0) continue;
    points.push(...(flip ? row.reverse() : row));
    flip = !flip;
  }
  return points;
}

/**
 * Expanding square: the standard pattern for searching outward from a datum,
 * where the most likely position is the middle and confidence falls off with
 * distance. Legs grow by one spacing every second turn.
 */
function expandingSquare(spec: PatternSpec): LatLon[] {
  const { center, radiusM, spacingM } = spec;
  const points: LatLon[] = [center];
  let east = 0;
  let north = 0;
  let leg = spacingM;
  let direction = 0; // 0 N, 1 E, 2 S, 3 W
  // Each corner is checked before it is committed. Stepping first and testing
  // afterwards lets the final leg finish well outside the area the operator
  // drew, which is the one thing a bounded search must not do.
  for (;;) {
    for (let repeat = 0; repeat < 2; repeat += 1) {
      const nextEast = direction === 1 ? east + leg : direction === 3 ? east - leg : east;
      const nextNorth = direction === 0 ? north + leg : direction === 2 ? north - leg : north;
      if (Math.max(Math.abs(nextEast), Math.abs(nextNorth)) > radiusM) return points;
      east = nextEast;
      north = nextNorth;
      direction = (direction + 1) % 4;
      points.push(offset(center, east, north));
    }
    leg += spacingM;
  }
}

/** An archimedean spiral: continuous outward coverage with no corners. */
function spiral(spec: PatternSpec): LatLon[] {
  const { center, radiusM, spacingM } = spec;
  const points: LatLon[] = [];
  // Sampled by angle that shrinks as the radius grows, so the chord between
  // consecutive points stays about one spacing the whole way out. A fixed
  // number of samples per turn is fine near the middle and leaves gaps at the
  // rim — and the gaps are exactly the ground nobody looked at.
  let angle = 0;
  for (;;) {
    const r = (spacingM * angle) / (2 * Math.PI);
    if (r > radiusM) break;
    points.push(offset(center, r * Math.sin(angle), r * Math.cos(angle)));
    // The step is not just the arc: the radius grows between samples too, so
    // the true chord is hypot(r·dθ, b·dθ) where b is the outward rate. Solving
    // that for one spacing keeps coverage honest at every radius — using r
    // alone leaves the outermost legs marginally too far apart. The 0.95 is
    // margin, because a gap is worse than a redundant waypoint.
    const outwardRate = spacingM / (2 * Math.PI);
    angle += Math.min(Math.PI / 8, (0.95 * spacingM) / Math.max(Math.hypot(r, outwardRate), 1));
    if (points.length > MAX_WAYPOINTS) break;
  }
  return points;
}

/**
 * Sector search: spokes out from the datum and back, rotating around it.
 * Used when the target is likely near the centre but the bearing is unknown.
 */
function sector(spec: PatternSpec): LatLon[] {
  const { center, radiusM, spacingM } = spec;
  // Spacing sets the arc between spokes at the rim, so coverage at the edge is
  // what the operator asked for rather than an arbitrary count.
  const spokes = Math.max(3, Math.min(36, Math.round((2 * Math.PI * radiusM) / spacingM)));
  const points: LatLon[] = [];
  for (let i = 0; i < spokes; i += 1) {
    const angle = (i / spokes) * 2 * Math.PI;
    points.push(center);
    points.push(offset(center, radiusM * Math.sin(angle), radiusM * Math.cos(angle)));
  }
  points.push(center);
  return points;
}

/** A closed ring at the radius — hold and observe one point. */
function orbit(spec: PatternSpec): LatLon[] {
  const { center, radiusM, spacingM } = spec;
  const steps = Math.max(8, Math.min(64, Math.round((2 * Math.PI * radiusM) / spacingM)));
  const points: LatLon[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const angle = (i / steps) * 2 * Math.PI;
    points.push(offset(center, radiusM * Math.sin(angle), radiusM * Math.cos(angle)));
  }
  return points;
}

const GENERATORS: Record<PatternKind, (spec: PatternSpec) => LatLon[]> = {
  grid,
  'expanding-square': expandingSquare,
  spiral,
  sector,
  orbit,
};

export const PATTERN_LABEL: Record<PatternKind, string> = {
  grid: 'GRID',
  'expanding-square': 'EXP SQUARE',
  spiral: 'SPIRAL',
  sector: 'SECTOR',
  orbit: 'ORBIT',
};

/**
 * The route for one pattern.
 *
 * Refuses rather than returning something unflyable: a spacing of zero is an
 * infinite loop, and a pattern of thousands of waypoints is one no autopilot
 * will accept and no operator meant to ask for.
 */
export function generatePattern(spec: PatternSpec): Waypoint[] {
  if (!(spec.spacingM > 0)) {
    throw new PatternError('Spacing must be greater than zero.');
  }
  if (!(spec.radiusM > 0)) {
    throw new PatternError('The area must have a size.');
  }
  if (spec.spacingM > spec.radiusM * 2) {
    throw new PatternError('Spacing is wider than the area — nothing would be covered.');
  }

  const points = GENERATORS[spec.kind](spec);
  if (points.length === 0) {
    throw new PatternError('That area and spacing produce no waypoints.');
  }
  if (points.length > MAX_WAYPOINTS) {
    throw new PatternError(
      `${points.length} waypoints is more than an autopilot will hold. Widen the spacing.`,
    );
  }
  return points.map((point) => waypoint(point, spec.altM));
}

export type SplitKind = 'strips' | 'sectors';

export const SPLIT_LABEL: Record<SplitKind, string> = {
  strips: 'STRIPS',
  sectors: 'SECTORS',
};

/**
 * Divide one search area between several aircraft.
 *
 * The whole pattern is generated first and then partitioned, rather than
 * generating a smaller pattern per aircraft. That is deliberate: the union of
 * the slices is exactly the route one aircraft would have flown, so the area
 * is covered once — no seam between two aircraft's boxes where the spacing
 * quietly doubles, and no strip flown twice because two sub-areas overlapped.
 *
 * `strips` splits by latitude band, which suits a lawnmower: each aircraft gets
 * whole passes and flies them the way the pattern intended.
 *
 * `sectors` splits by bearing from the centre, which suits searching outward
 * from a datum — every aircraft starts near the middle and works its own wedge.
 */
export function divideForFleet(
  spec: PatternSpec,
  count: number,
  split: SplitKind = 'strips',
): Waypoint[][] {
  if (!Number.isInteger(count) || count < 1) {
    throw new PatternError('Aircraft count must be a whole number of at least one.');
  }
  const route = generatePattern(spec);
  if (count === 1) return [route];
  if (count > route.length) {
    throw new PatternError(
      `${count} aircraft for ${route.length} waypoints leaves some with nothing to fly.`,
    );
  }

  const buckets: Waypoint[][] = Array.from({ length: count }, () => []);

  if (split === 'sectors') {
    // A degree of longitude is shorter than a degree of latitude everywhere but
    // the equator, so a bearing taken from raw degree differences is not the
    // bearing on the ground: at 36° it skews by about 1.24×, and the wedges come
    // out visibly unequal. Seen in the field as a three-way split where one
    // aircraft drew 10.6 km and another 0.8 km of the same pattern. offset()
    // applies this correction on the way out; taking it back off on the way in
    // is what makes the two agree.
    const cosLat = Math.cos((spec.center.lat * Math.PI) / 180);
    for (const wp of route) {
      const east = (wp.lon - spec.center.lon) * cosLat;
      const north = wp.lat - spec.center.lat;
      // Bearing from the datum, 0..2π, mapped onto the wedges.
      let angle = Math.atan2(east, north);
      if (angle < 0) angle += 2 * Math.PI;
      const index = Math.min(count - 1, Math.floor((angle / (2 * Math.PI)) * count));
      buckets[index]!.push(wp);
    }
  } else {
    // Bands by latitude. Ordering the route by band keeps each aircraft's
    // waypoints in the sequence the pattern laid them, so a lawnmower stays a
    // lawnmower rather than becoming a scatter of points in row order.
    const lats = route.map((wp) => wp.lat);
    const min = Math.min(...lats);
    const span = Math.max(...lats) - min || 1;
    for (const wp of route) {
      const index = Math.min(count - 1, Math.floor(((wp.lat - min) / span) * count));
      buckets[index]!.push(wp);
    }
  }

  const empty = buckets.filter((bucket) => bucket.length === 0).length;
  if (empty > 0) {
    throw new PatternError(
      `That split leaves ${empty} aircraft with nothing to fly. Use fewer aircraft or a wider area.`,
    );
  }
  return buckets;
}
